import { TemplateCategory } from "@/components/confluence/templates/template-categories-search";
import {
    Author,
    Template,
    TemplateType
} from "@/content/templates/template.types";
import {
    CategoryKey,
    CategoryOrder
} from "@/templates/software/confluence/templates/confluence-templates-page/src/config/category-template-order.ts";

export function isAuthor(template: Template): boolean {
    return (
        (template.contributor as Author)?.organization !== undefined ||
        (template.contributor as Author)?.jobTitle !== undefined
    );
}

/**
 * Returns the author detail for a template
 * `name, jobTitle @ organization`
 * @param template
 */
export function getTemplateByLine(template: Template): string | undefined {
    const author = isAuthor(template) ? (template.contributor as Author) : null;
    // Default to name from contributor if author is not available
    let contributorName = template.contributor?.name;
    if (author) {
        const authorDetail = getTemplateAuthorDetail(author);
        if (authorDetail) {
            contributorName += `, ${authorDetail}`;
        }
    }
    return contributorName ?? undefined;
}

/**
 * Returns the author detail for a template
 * `jobTitle @ organization`
 * @param author
 */
export function getTemplateAuthorDetail(author: Author): string | undefined {
    // Default to name from contributor if author is not available
    let authorDetail = author.jobTitle;
    if (!authorDetail) {
        return undefined;
    }
    if (author.organization?.name) {
        if (authorDetail) {
            authorDetail += " @ ";
        }
        authorDetail += author.organization.name;
    }
    return authorDetail ?? undefined;
}

/**
 * Returns the sorted categories based on the order defined in `CategoryOrder`
 * @param categoriesToDisplay
 */
export function getUnifiedSortedCategories(
    categoriesToDisplay: TemplateCategory[]
): TemplateCategory[] {
    return categoriesToDisplay.sort((a, b) => {
        const indexA =
            CategoryOrder[a.urlSlug as CategoryKey] !== undefined
                ? CategoryOrder[a.urlSlug as CategoryKey]
                : Number.MAX_VALUE;
        const indexB =
            CategoryOrder[b.urlSlug as CategoryKey] !== undefined
                ? CategoryOrder[b.urlSlug as CategoryKey]
                : Number.MAX_VALUE;
        return indexA - indexB;
    });
}

/**
 * Returns the template type object with type / label
 * @param template
 */
export function getTemplateType(template: Template): {
    type: TemplateType;
    label: string;
} {
    const templateType = template.templateType
        ? template.templateType[0]
        : { id: null, templateTypeName: null };

    return {
        type:
            templateType?.id === "1HlskYfRNciSnQ02SDhCPs"
                ? TemplateType.Whiteboard
                : TemplateType.Page,

        label: templateType?.templateTypeName ?? TemplateType.Page
    };
}
